import { $color, $shadow } from "@/utils/cssVarUtils"
import { modalAnatomy as parts } from "@chakra-ui/anatomy"
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleFunction,
  SystemStyleObject,
} from "@chakra-ui/theme-tools"


const baseStyleOverlay: SystemStyleFunction = (props) => {
  return {
    bg: "blackAlpha.600",
    zIndex: "modal",
  }
}

const baseStyleDialogContainer: SystemStyleFunction = (props) => {
  const { isCentered, scrollBehavior } = props

  return {
    display: "flex",
    zIndex: "modal",
    justifyContent: "center",
    my: '20%',
    alignItems: isCentered ? "center" : "flex-start",
    overflow: scrollBehavior === "inside" ? "hidden" : "auto",
  }
}

const baseStyleDialog: SystemStyleFunction = (props) => {
  const { scrollBehavior } = props

  return {
    borderRadius: "2xs",
    bg: $color('bg-tooltip-normal'),
    color: "inherit",
    zIndex: "modal",
    my: 0,
    maxH: scrollBehavior === "inside" ? "100%" : undefined,
    boxShadow: $shadow('normal'),
  }
}

const baseStyleHeader: SystemStyleObject = {
  p: 'md',
  h: '7xl',
  fontSize: "md",
  fontWeight: "semibold",
  borderBottom: '1px solid',
  borderBottomColor: $color('scene-border'),
  textAlign: 'center',
}

const baseStyleCloseButton: SystemStyleObject = {
  position: "absolute",
  top: 'md',
  right: 'md',
}

const baseStyleBody: SystemStyleFunction = (props) => {
  const { scrollBehavior } = props
  return {
    p: 'md',
    flex: 1,
    overflow: scrollBehavior === "inside" ? "auto" : undefined,
  }
}

const baseStyleFooter: SystemStyleObject = {
  p: 'md',
}

const baseStyle: PartsStyleFunction<typeof parts> = (props) => ({
  overlay: baseStyleOverlay(props),
  dialogContainer: baseStyleDialogContainer(props),
  dialog: baseStyleDialog(props),
  header: baseStyleHeader,
  closeButton: baseStyleCloseButton,
  body: baseStyleBody(props),
  footer: baseStyleFooter,
})

/**
 * Since the `maxWidth` prop references theme.sizes internally,
 * we can leverage that to size our modals.
 */
function getSize(value: string): PartsStyleObject<typeof parts> {
  if (value === "full") {
    return {
      dialog: {
        maxW: "100vw",
        minH: "100vh",
        "@supports(min-height: -webkit-fill-available)": {
          minH: "-webkit-fill-available",
        },
        my: 0,
      },
    }
  }
  return {
    dialog: { maxW: value },
  }
}

const sizes = {
  xs: getSize("container.xs"),
  sm: getSize("container.sm"),
  md: getSize("container.md"),
  lg: getSize("container.lg"),
  xl: getSize("container.xl"),
  "2xl": getSize("container.2xl"),
  "3xl": getSize("container.3xl"),
  "4xl": getSize("container.4xl"),
  "5xl": getSize("container.5xl"),
  "6xl": getSize("container.6xl"),
  full: getSize("full"),
}

const defaultProps = {
  size: "lg",
}

export const Modal = {
  parts: parts.keys,
  baseStyle,
  sizes,
  defaultProps,
}